import type { Participant } from '@seen/core/models/patients/participant'
import { Button, cn, TypographyDetail, TypographySubtle } from '@seen/ui'
import { IconPencil } from '@tabler/icons-react'
import { type JSX, useEffect, useState } from 'react'
import { useMatch } from 'react-router'
import { z } from 'zod'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogButton } from '#src/components/core/dialog/DialogButton.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { MoreDropdownMenu } from '#src/components/MoreDropdownMenu.tsx'
import { DisenrollDropdownMenuItem } from '#src/components/patient/detail-content/contact-tab/DisenrollmentDate.tsx'
import { useForm } from '#src/hooks/useForm.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'

const INTAKE_ROUTE = '/intake/*' as const

export function EnrollmentDateDisplay({
  participant,
}: {
  participant: Participant
}): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const isIntakeView = !!useMatch(INTAKE_ROUTE)
  const [open, setOpen] = useState(false)
  const patchParticipant = usePatchMutation<Participant>()

  const form = useForm({
    schema: z.object({
      enrollmentDate: z.string().nullable(),
    }),
    defaultValues: {
      enrollmentDate: participant.enrollmentDateString,
    },
  })

  useEffect(() => {
    form.reset({
      enrollmentDate: participant.enrollmentDateString,
    })
  }, [participant, form])

  const onSubmit = form.handleSubmit((values) => {
    patchParticipant.mutate(
      {
        instance: participant,
        mutateFn: (resource) => {
          resource.enrollmentDate = values.enrollmentDate
        },
      },
      {
        onSuccess: () => {
          setOpen(false)
          form.reset()
        },
      }
    )
  })

  return (
    <div className="group flex items-center gap-1">
      <div className="flex flex-1 flex-col">
        <TypographyDetail className="leading-none font-semibold text-zinc-950">
          Enrollment Date
        </TypographyDetail>
        <TypographySubtle
          className={cn(
            'py-1.5',
            !participant.enrollmentDateString && 'text-foreground-tertiary'
          )}
        >
          {participant.enrollmentDateString ?? 'Not enrolled'}
        </TypographySubtle>
      </div>
      {isIntakeView && !isReadOnly && (
        <div className="invisible flex group-hover:visible">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogButton variant="ghost" size="icon">
              <IconPencil className="size-4" />
            </DialogButton>
            <DialogDisplay>
              <DialogHeader>Edit Enrollment Date</DialogHeader>
              <DialogContent>
                <Form onSubmit={onSubmit} className="flex flex-col gap-4">
                  <FormField
                    control={form.control}
                    name="enrollmentDate"
                    render={({ field }) => (
                      <DatePicker {...field} allowUndefined />
                    )}
                  />
                  <Button type="submit" loading={patchParticipant.isPending}>
                    Save
                  </Button>
                </Form>
              </DialogContent>
            </DialogDisplay>
          </Dialog>
        </div>
      )}
      {!isReadOnly && (
        <div className="invisible group-hover:visible has-data-[state=open]:visible">
          <MoreDropdownMenu>
            <DisenrollDropdownMenuItem participant={participant} />
          </MoreDropdownMenu>
        </div>
      )}
    </div>
  )
}
